import React from "react";
import { motion } from "framer-motion";
import {
	Shield,
	CheckCircle,
	XCircle,
	AlertTriangle,
} from "lucide-react";
import { getScoreColorClass } from "../gradeColors";

const ENTITY_METRICS = [
	{ key: "entity_completeness_score", label: "Entity Completeness" },
	{ key: "audience_clarity_score", label: "Audience Clarity" },
	{ key: "category_match_score", label: "Category Match" },
	{ key: "trust_signal_awareness_score", label: "Trust Signals" },
];

const EVIDENCE_LIST_CAP = 8;

function mergeUniqueStrings(arrays) {
	const seen = new Set();
	const result = [];
	for (const arr of arrays) {
		for (const item of arr || []) {
			if (item && !seen.has(item)) {
				seen.add(item);
				result.push(item);
			}
		}
	}
	return result;
}

function getSiteWideSignals(providers) {
	const first = providers[0] || {};
	return {
		brand_name_detected: first.brand_name_detected,
		primary_offering_detected: first.primary_offering_detected,
		entity_completeness_score: first.entity_completeness_score,
		audience_clarity_score: first.audience_clarity_score,
		category_match_score: first.category_match_score,
		trust_signal_awareness_score: first.trust_signal_awareness_score,
	};
}

function StatusItem({ label, detected }) {
	return (
		<div className="report-section__status-item">
			<span>{label}</span>
			{detected ? (
				<CheckCircle className="report-section__status-icon report-section__status-icon--ok" />
			) : (
				<XCircle className="report-section__status-icon report-section__status-icon--fail" />
			)}
		</div>
	);
}

function EntityMetricCard({ label, score, index }) {
	const value = Number(score ?? 0);

	return (
		<motion.div
			className="brand-recognition__metric-card"
			initial={{ opacity: 0, y: 12 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.35, delay: index * 0.08 }}
		>
			<p className="brand-recognition__metric-label">{label}</p>
			<p className="brand-recognition__metric-score">{value.toFixed(0)}</p>
			<div className="report-section__metric-bar brand-recognition__metric-bar">
				<motion.div
					className="brand-recognition__metric-bar-fill"
					initial={{ width: 0 }}
					animate={{ width: `${value}%` }}
					transition={{ duration: 0.7, delay: index * 0.08 + 0.2 }}
				/>
			</div>
		</motion.div>
	);
}

export default function BrandRecognitionReport({ report, entitySignals }) {
	if (!report?.providers?.length) return null;

	const siteWide = getSiteWideSignals(report.providers);
	const evidence = mergeUniqueStrings(report.providers.map((p) => p.evidence)).slice(
		0,
		EVIDENCE_LIST_CAP
	);
	const missingSignals = mergeUniqueStrings(
		report.providers.map((p) => p.missing_signals)
	).slice(0, EVIDENCE_LIST_CAP);

	const brandNameLabel = entitySignals?.brand_name
		? `${entitySignals.brand_name} detected`
		: "Brand Name";

	return (
		<motion.div
			className="report-section"
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.5 }}
		>
			<div className="report-section__header">
				<div className="report-section__icon-wrap report-section__icon-wrap--blue">
					<Shield className="report-section__icon" />
				</div>
				<div className="report-section__header-text">
					<h2 className="report-section__title">Brand Recognition Analysis</h2>
					<p className="report-section__subtitle">
						How AI models identify and understand your brand
					</p>
				</div>
			</div>

			<div className="report-section__grid">
				{report.providers.map((provider, index) => (
					<motion.div
						key={provider.provider}
						className="report-section__provider-card"
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.4, delay: index * 0.1 }}
					>
						<h3 className="report-section__provider-name">{provider.provider}</h3>

						<div className="report-section__score-display">
							<span
								className={`report-section__score-value ${getScoreColorClass(provider.brand_score)}`}
							>
								{Number(provider.brand_score).toFixed(0)}
							</span>
							<span className="report-section__score-max">/ 100</span>
						</div>

						<div className="report-section__progress-track brand-recognition__provider-progress">
							<motion.div
								className="report-section__progress-fill"
								initial={{ width: 0 }}
								animate={{ width: `${provider.brand_score}%` }}
								transition={{ duration: 0.8, delay: index * 0.1 + 0.3 }}
							/>
						</div>
					</motion.div>
				))}
			</div>

			<p className="brand-recognition__footnote">
				Each model&apos;s rating reflects how well that AI identifies your brand,
				independent of site-wide entity signals.
			</p>

			<div className="brand-recognition__entity-block">
				<h3 className="brand-recognition__entity-title">Brand Entity Signals</h3>

				<div className="report-section__status-list brand-recognition__status-list">
					<StatusItem label={brandNameLabel} detected={siteWide.brand_name_detected} />
					{/* <StatusItem
						label="Primary Offering"
						detected={siteWide.primary_offering_detected}
					/> */}
				</div>

				<div className="brand-recognition__metric-grid">
					{ENTITY_METRICS.map((metric, index) => (
						<EntityMetricCard
							key={metric.key}
							label={metric.label}
							score={siteWide[metric.key]}
							index={index}
						/>
					))}
				</div>

				<p className="brand-recognition__site-wide-label">
					Site-wide — same for all models
				</p>
			</div>

			{(evidence.length > 0 || missingSignals.length > 0) && (
				<div className="report-section__insights-grid brand-recognition__evidence-grid">
					{evidence.length > 0 && (
						<div>
							<p className="report-section__list-label report-section__list-label--success">
								Evidence Found
							</p>
							<ul className="report-section__bullet-list">
								{evidence.map((item, i) => (
									<li key={i} className="report-section__bullet-item">
										<CheckCircle className="report-section__bullet-icon report-section__status-icon--ok" />
										<span>{item}</span>
									</li>
								))}
							</ul>
						</div>
					)}

					{missingSignals.length > 0 && (
						<div>
							<p className="report-section__list-label report-section__list-label--warning">
								Missing Signals
							</p>
							<ul className="report-section__bullet-list">
								{missingSignals.map((item, i) => (
									<li key={i} className="report-section__bullet-item">
										<XCircle className="report-section__bullet-icon report-section__status-icon--fail" />
										<span>{item}</span>
									</li>
								))}
							</ul>
						</div>
					)}
				</div>
			)}

			{report.consensus_summary && (
				<div className="report-section__callout report-section__callout--blue">
					<h3 className="report-section__callout-title">
						<Shield
							className="report-section__callout-icon citation-tracking__methodology-icon"
							style={{ color: "#2563eb" }}
						/>
						Consensus Summary
					</h3>
					<p className="report-section__callout-text">{report.consensus_summary}</p>
				</div>
			)}

			{report.global_gaps?.length > 0 && (
				<div className="report-section__callout report-section__callout--orange mb-4">
					<h3 className="report-section__callout-title">
						<AlertTriangle
							className="report-section__callout-icon"
							style={{ color: "#ea580c" }}
						/>
						Critical Gaps Across All Models
					</h3>
					<ul className="report-section__numbered-list">
						{report.global_gaps.map((gap, i) => (
							<li key={i} className="report-section__bullet-item">
								<span style={{ color: "#f97316", fontWeight: 700 }}>•</span>
								<span>{gap}</span>
							</li>
						))}
					</ul>
				</div>
			)}
		</motion.div>
	);
}
